import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getInvoices, deleteInvoice, updateInvoice, downloadInvoicePdf, duplicateInvoice, exportInvoicesCsv, sendInvoiceEmail, getSettings, generatePortalLink } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Plus, 
  Search, 
  MoreHorizontal, 
  Download, 
  Pencil, 
  Trash2,
  FileText,
  CheckCircle,
  Printer,
  Eye,
  Copy,
  FileDown,
  Send,
  XCircle,
  Loader2,
  Mail,
  QrCode,
  Link2
} from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (amount, currency = 'CHF') => {
  return new Intl.NumberFormat('fr-CH', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('fr-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const StatusBadge = ({ status, t }) => {
  const styles = {
    paid: 'badge-paid',
    sent: 'bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-0.5 rounded-full text-xs font-medium',
    draft: 'bg-slate-50 text-slate-700 border border-slate-200 px-2.5 py-0.5 rounded-full text-xs font-medium',
    overdue: 'badge-overdue',
    cancelled: 'bg-slate-100 text-slate-500 border border-slate-200 px-2.5 py-0.5 rounded-full text-xs font-medium'
  };
  const getLabel = (status) => {
    if (t) {
      switch(status) {
        case 'paid': return t('invoices.paid');
        case 'sent': return t('invoices.sent');
        case 'draft': return t('invoices.draft');
        case 'overdue': return t('invoices.overdue');
        case 'cancelled': return t('invoices.cancelled');
        default: return status;
      }
    }
    return status;
  };
  return <span className={styles[status] || styles.draft}>{getLabel(status)}</span>;
};

const InvoicesPage = () => {
  const { t, language } = useLanguage();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailInvoice, setEmailInvoice] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [emailForm, setEmailForm] = useState({ to_email: '', to_name: '', subject: '', body_text: '' });
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrInvoice, setQrInvoice] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const getLocale = () => {
    switch(language) {
      case 'de': return 'de-CH';
      case 'en': return 'en-GB';
      default: return 'fr-CH';
    }
  };

  const formatCurrencyLocal = (amount, currency = 'CHF') => {
    return new Intl.NumberFormat(getLocale(), {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDateLocal = (dateString) => {
    return new Date(dateString).toLocaleDateString(getLocale(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  useEffect(() => {
    const statusParam = searchParams.get('status');
    if (statusParam) {
      setStatusFilter(statusParam);
    }
    loadInvoices();
    checkSMTPConfig();
  }, [searchParams]);

  const checkSMTPConfig = async () => {
    try {
      const response = await getSettings();
      setSmtpConfigured(response.data.smtp_configured || false);
    } catch (error) {
      console.error('Error checking SMTP config');
    }
  };

  const loadInvoices = async () => {
    try {
      const response = await getInvoices();
      setInvoices(response.data);
    } catch (error) {
      toast.error(t('common.loading') + ' error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
    try {
      const response = await downloadInvoicePdf(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `facture_${invoiceNumber}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF téléchargé dans votre dossier Téléchargements');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleViewPdf = async (invoiceId) => {
    try {
      const response = await downloadInvoicePdf(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Erreur lors de l\'ouverture');
    }
  };

  const handlePrintPdf = async (invoiceId) => {
    try {
      const response = await downloadInvoicePdf(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error) {
      toast.error('Erreur lors de l\'impression');
    }
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    try {
      await updateInvoice(invoiceId, { status: newStatus });
      toast.success(`Facture marquée comme "${newStatus === 'paid' ? 'payée' : newStatus === 'sent' ? 'envoyée' : newStatus === 'cancelled' ? 'annulée' : newStatus}"`);
      loadInvoices();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDuplicate = async (invoiceId) => {
    try {
      const response = await duplicateInvoice(invoiceId);
      toast.success(`Facture dupliquée: ${response.data.invoice_number}`);
      loadInvoices();
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleSendEmail = (invoice) => {
    if (!smtpConfigured) {
      // Fallback to mailto if SMTP not configured
      const clientEmail = invoice.client?.email || '';
      const subject = encodeURIComponent(`Facture ${invoice.invoice_number} - ${invoice.total} ${invoice.currency || 'CHF'}`);
      const body = encodeURIComponent(
        `Bonjour,\n\n` +
        `Veuillez trouver ci-joint la facture ${invoice.invoice_number} d'un montant de ${formatCurrency(invoice.total, invoice.currency || 'CHF')}.\n\n` +
        `Date d'échéance: ${invoice.due_date ? formatDate(invoice.due_date) : 'Non spécifiée'}\n\n` +
        `Merci de votre confiance.\n\n` +
        `Cordialement`
      );
      window.location.href = `mailto:${clientEmail}?subject=${subject}&body=${body}`;
      toast.info('Configurez votre SMTP dans Paramètres > Email pour envoyer directement.', { duration: 5000 });
      return;
    }

    // Open email dialog for direct sending
    setEmailInvoice(invoice);
    setEmailForm({
      to_email: invoice.client?.email || '',
      to_name: invoice.client?.name || '',
      subject: `Facture ${invoice.invoice_number} - ${formatCurrency(invoice.total, invoice.currency || 'CHF')}`,
      body_text: `Bonjour${invoice.client?.name ? ' ' + invoice.client.name : ''},\n\nVeuillez trouver ci-joint la facture ${invoice.invoice_number} d'un montant de ${formatCurrency(invoice.total, invoice.currency || 'CHF')}.\n\nDate d'échéance: ${invoice.due_date ? formatDate(invoice.due_date) : 'À votre convenance'}\n\nMerci de votre confiance.\n\nCordialement`
    });
    setEmailDialogOpen(true);
  };

  const handleSendEmailSubmit = async () => {
    if (!emailForm.to_email) {
      toast.error('Veuillez saisir une adresse email');
      return;
    }
    setSendingEmail(true);
    try {
      await sendInvoiceEmail(emailInvoice.id, {
        to_email: emailForm.to_email,
        to_name: emailForm.to_name,
        subject: emailForm.subject,
        body_text: emailForm.body_text,
        document_type: 'invoice',
        document_id: emailInvoice.id
      });
      toast.success('Facture envoyée par email avec succès !');
      setEmailDialogOpen(false);
      loadInvoices(); // Refresh to update status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleExportCsv = async () => {
    try {
      const response = await exportInvoicesCsv();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'factures_export.csv';
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export CSV téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const handleDelete = async () => {
    if (!invoiceToDelete) return;
    try {
      await deleteInvoice(invoiceToDelete);
      toast.success('Facture supprimée');
      loadInvoices();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteDialogOpen(false);
      setInvoiceToDelete(null);
    }
  };

  const filteredInvoices = invoices.filter((invoice) => {
    const clientName = invoice.client?.name || invoice.client_name || '';
    const matchesSearch = 
      invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      clientName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900">
            {t('invoices.title')}
          </h1>
          <p className="text-slate-500 mt-1">
            {language === 'fr' ? 'Gérez toutes vos factures' : language === 'de' ? 'Verwalten Sie alle Ihre Rechnungen' : 'Manage all your invoices'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={handleExportCsv}
          >
            <FileDown className="w-4 h-4 mr-2" />
            {t('invoices.exportCsv')}
          </Button>
          <Button 
            onClick={() => navigate('/app/invoices/manual')}
            variant="outline"
            data-testid="manual-invoice-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Facture manuelle
          </Button>
          <Button 
            onClick={() => navigate('/app/invoices/new')}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="new-invoice-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('invoices.create')}
          </Button>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder={t('invoices.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                <SelectValue placeholder={t('invoices.filterByStatus')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')}</SelectItem>
                <SelectItem value="draft">{t('invoices.draft')}</SelectItem>
                <SelectItem value="sent">{t('invoices.sent')}</SelectItem>
                <SelectItem value="paid">{t('invoices.paid')}</SelectItem>
                <SelectItem value="overdue">{t('invoices.overdue')}</SelectItem>
                <SelectItem value="cancelled">{t('invoices.cancelled')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {filteredInvoices.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="px-6 py-4">{t('invoices.number')}</th>
                    <th className="px-6 py-4">{t('invoices.client')}</th>
                    <th className="px-6 py-4">{t('invoices.date')}</th>
                    <th className="px-6 py-4">{t('invoices.dueDate')}</th>
                    <th className="px-6 py-4">{t('invoices.amount')}</th>
                    <th className="px-6 py-4">{t('invoices.status')}</th>
                    <th className="px-6 py-4 text-right">{t('invoices.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((invoice) => (
                    <tr key={invoice.id} data-testid={`invoice-row-${invoice.id}`}
                      className="cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => handleViewPdf(invoice.id)}
                    >
                      <td className="px-6 py-4 font-medium text-slate-900">
                        {invoice.invoice_number}
                      </td>
                      <td className="px-6 py-4 text-slate-600">
                        {invoice.client?.name || invoice.client_name || '-'}
                      </td>
                      <td className="px-6 py-4 text-slate-600">
                        {formatDateLocal(invoice.created_at)}
                      </td>
                      <td className="px-6 py-4 text-slate-600">
                        {invoice.due_date ? formatDateLocal(invoice.due_date) : '-'}
                      </td>
                      <td className="px-6 py-4 font-medium text-slate-900">
                        {formatCurrencyLocal(invoice.total, invoice.currency)}
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge status={invoice.status} t={t} />
                      </td>
                      <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`invoice-menu-${invoice.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewPdf(invoice.id)}>
                              <Eye className="w-4 h-4 mr-2" />
                              {t('common.preview')} PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownloadPdf(invoice.id, invoice.invoice_number)}>
                              <Download className="w-4 h-4 mr-2" />
                              {t('invoices.downloadPdf')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handlePrintPdf(invoice.id)}>
                              <Printer className="w-4 h-4 mr-2" />
                              {t('common.print')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleSendEmail(invoice)}>
                              <Send className="w-4 h-4 mr-2" />
                              {t('invoices.sendByEmail')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { setQrInvoice(invoice); setQrDialogOpen(true); }}>
                              <QrCode className="w-4 h-4 mr-2" />
                              {t('invoices.viewQr')}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => navigate(`/app/invoices/${invoice.id}`)}>
                              <Eye className="w-4 h-4 mr-2" />
                              {t('common.details')}
                            </DropdownMenuItem>
                            {invoice.status === 'draft' && (
                              <DropdownMenuItem onClick={() => navigate(`/app/invoices/${invoice.id}/edit`)}>
                                <Pencil className="w-4 h-4 mr-2" />
                                {t('common.edit')}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleDuplicate(invoice.id)}>
                              <Copy className="w-4 h-4 mr-2" />
                              {t('common.duplicate')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={async () => {
                              try {
                                const res = await generatePortalLink(invoice.id);
                                const url = `${window.location.origin}${res.data.url}`;
                                await navigator.clipboard.writeText(url);
                                toast.success('Lien portail copie !');
                              } catch (err) { toast.error('Erreur'); }
                            }}>
                              <Link2 className="w-4 h-4 mr-2" />
                              Copier lien portail
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {invoice.status === 'draft' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(invoice.id, 'sent')}>
                                <Send className="w-4 h-4 mr-2" />
                                {t('invoices.markAsSent')}
                              </DropdownMenuItem>
                            )}
                            {(invoice.status === 'sent' || invoice.status === 'overdue') && (
                              <DropdownMenuItem onClick={() => handleStatusChange(invoice.id, 'paid')}>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                {t('invoices.markAsPaid')}
                              </DropdownMenuItem>
                            )}
                            {invoice.status !== 'cancelled' && invoice.status !== 'paid' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(invoice.id, 'cancelled')}>
                                <XCircle className="w-4 h-4 mr-2" />
                                {t('common.cancel')}
                              </DropdownMenuItem>
                            )}
                            {invoice.status === 'draft' && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => {
                                    setInvoiceToDelete(invoice.id);
                                    setDeleteDialogOpen(true);
                                  }}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  {t('common.delete')}
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state py-12">
              <FileText className="empty-state-icon" />
              <h3 className="empty-state-title">{t('invoices.noInvoices')}</h3>
              <p className="empty-state-description">
                {searchTerm || statusFilter !== 'all' 
                  ? (language === 'fr' ? 'Aucune facture ne correspond à vos critères' : language === 'de' ? 'Keine Rechnung entspricht Ihren Kriterien' : 'No invoice matches your criteria')
                  : t('invoices.createFirst')}
              </p>
              {!searchTerm && statusFilter === 'all' && (
                <Button 
                  onClick={() => navigate('/app/invoices/new')}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('invoices.create')}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('invoices.deleteConfirm')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('invoices.deleteWarning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Envoyer la facture par email
            </DialogTitle>
            <DialogDescription>
              {emailInvoice?.invoice_number} - {emailInvoice && formatCurrency(emailInvoice.total, emailInvoice.currency || 'CHF')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email destinataire *</Label>
                <Input 
                  type="email"
                  value={emailForm.to_email}
                  onChange={(e) => setEmailForm({ ...emailForm, to_email: e.target.value })}
                  placeholder="client@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input 
                  value={emailForm.to_name}
                  onChange={(e) => setEmailForm({ ...emailForm, to_name: e.target.value })}
                  placeholder="Nom du client"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Sujet</Label>
              <Input 
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea 
                value={emailForm.body_text}
                onChange={(e) => setEmailForm({ ...emailForm, body_text: e.target.value })}
                rows={6}
              />
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>📎 Pièce jointe :</strong> Le PDF de la facture sera automatiquement attaché.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSendEmailSubmit} 
              disabled={sendingEmail}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {sendingEmail ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Envoi en cours...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" />Envoyer</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR-bill Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-red-600" />
              QR-facture suisse
            </DialogTitle>
            <DialogDescription>
              {qrInvoice?.invoice_number} - {qrInvoice && formatCurrency(qrInvoice.total, qrInvoice.currency || 'CHF')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <QrBillImage invoiceId={qrInvoice?.id} />
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
              <p className="font-medium mb-1">📱 Comment utiliser ce QR-code ?</p>
              <p>Vos clients peuvent le scanner avec leur application bancaire suisse pour effectuer le paiement directement, sans saisir manuellement les données.</p>
            </div>
            <div className="flex justify-center">
              <Button 
                onClick={async () => {
                  try {
                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${qrInvoice.id}/qr-bill`, {
                      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                    });
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `qr-facture-${qrInvoice.invoice_number}.png`;
                    link.click();
                    window.URL.revokeObjectURL(url);
                  } catch (error) {
                    toast.error('Erreur lors du téléchargement');
                  }
                }}
              >
                <Download className="w-4 h-4 mr-2" />
                Télécharger le QR-code
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// QR Bill Image Component with authentication
const QrBillImage = ({ invoiceId }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!invoiceId) return;
    
    const fetchQrImage = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${invoiceId}/qr-bill`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch QR');
        }
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        setImageSrc(url);
        setError(false);
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    
    fetchQrImage();
    
    return () => {
      if (imageSrc) {
        window.URL.revokeObjectURL(imageSrc);
      }
    };
  }, [invoiceId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !imageSrc) {
    return (
      <div className="w-64 h-64 mx-auto bg-slate-100 rounded-lg flex flex-col items-center justify-center text-slate-500 text-center p-4">
        <QrCode className="w-10 h-10 mb-2 text-slate-400" />
        <p>QR-code non disponible.</p>
        <p className="text-sm mt-2">Vérifiez que l'IBAN est configuré dans les paramètres.</p>
      </div>
    );
  }

  return (
    <div className="flex justify-center">
      <img 
        src={imageSrc}
        alt="QR-facture"
        className="w-64 h-64 border border-slate-200 rounded-lg"
      />
    </div>
  );
};

export default InvoicesPage;
